import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import { portalApi, portalToken, formatApiError } from "./api";
import { BRAND } from "@/data/brand";

export default function PortalInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    portalApi.getInvitation(token).then(setInvitation).catch((e) => {
      const msg = e?.response?.data?.detail || "This invitation is not valid.";
      setError(msg);
    });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    if (!accept) return toast.error("Please accept the Terms of Use");
    setBusy(true);
    try {
      const { token: jwt, user } = await portalApi.acceptInvitation(token, password);
      portalToken.set(jwt);
      toast.success("Welcome to AG Public Adjusting");
      nav(user.role === "adjuster" ? "/digital-card" : user.role === "super_admin" ? "/digital-card/admin" : "/digital-card/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <main className="min-h-screen bg-midnight-deeper flex items-center justify-center px-6" data-testid="invite-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-6" />
          <h1 className="font-display-tight text-white text-3xl mb-4">Invitation Unavailable</h1>
          <p className="text-ag-text-secondary text-sm mb-8">{error}</p>
          <Link to="/" className="btn-gold inline-flex items-center gap-2 !py-2.5 !px-5">Return to AG Public Adjusting</Link>
        </div>
      </main>
    );
  }
  if (!invitation) return <main className="min-h-screen bg-midnight-deeper flex items-center justify-center text-ag-text-muted font-mono text-xs tracking-[0.3em]">LOADING…</main>;

  return (
    <main data-testid="invite-accept" className="min-h-screen bg-midnight-deeper px-6 py-12 flex flex-col justify-center">
      <div className="max-w-md w-full mx-auto">
        <Link to="/" className="flex items-center justify-center mb-10">
          <img src={BRAND.logoWhite} alt="AG Public Adjusting" className="h-12 w-auto object-contain" />
        </Link>
        <h1 className="font-display-tight text-white text-[clamp(2rem,7vw,3rem)] tracking-[-0.04em] leading-[1] mb-4 text-center">
          Welcome to<br /><span className="gold-metallic font-display font-light">AG Public Adjusting</span>
        </h1>
        <p className="text-ag-text-secondary text-center text-sm mb-8 font-light">
          {invitation.first_name}, create your account to build your Digital Card.
        </p>

        <form onSubmit={submit} className="glass-strong p-6 sm:p-8 space-y-5" data-testid="invite-form">
          <div>
            <div className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] uppercase mb-1">Email</div>
            <div className="text-white text-sm font-light">{invitation.email}</div>
          </div>
          <label className="block">
            <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">Create Password (min 8)</span>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="ag-input" data-testid="invite-password" autoComplete="new-password" />
          </label>
          <label className="block">
            <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">Confirm Password</span>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="ag-input" data-testid="invite-confirm" autoComplete="new-password" />
          </label>
          <label className="flex items-start gap-3 text-xs text-ag-text-secondary font-light cursor-pointer">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1" data-testid="invite-accept-terms" />
            <span>I accept the AG Public Adjusting Terms of Use and confirm my professional information will remain accurate.</span>
          </label>
          <button type="submit" disabled={busy} className="btn-gold w-full inline-flex items-center justify-center gap-2 !py-3.5 disabled:opacity-50" data-testid="invite-submit">
            <ShieldCheck className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create Account"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>
    </main>
  );
}
